import * as React from 'react'

import EditButton from './EditButton'
import { stripSpecialCharacters } from '../utils/string'

import {
  Box,
  TextField as MaterialTextField,
  Icon,
  Typography,
  FormControl,
  styled,
} from '@design/pt-ui'
const HeaderIconEdit = styled(Icon)({
  color: '#000',
  fontSize: '20px !important',
  marginRight: 5,
})

const CounterBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'warning',
})<{ warning?: boolean }>(({ theme, warning }) => ({
  position: 'absolute',
  fontSize: 14,
  backgroundColor: warning ? theme.palette.warning.light : '#57ac40',
  marginLeft: theme.spacing(2),
  color: '#fff',
  padding: '2px 5px',
  fontWeight: 700,
  zIndex: 10,
  right: 0,
  top: '-10px',
  borderRadius: 4,
  lineHeight: '19px',
}))

interface IEditableProps {
  label?: string
  value: string
  maxLength?: number
  saveOnBlur?: boolean
  onSave?: (value: string) => void
}

interface IEditableState {
  value: null | string
  editing: boolean
}

export function Editable(props: IEditableProps) {
  let _canceled = false
  let _saved = false

  const [state, setState] = React.useState<IEditableState>({
    value: null,
    editing: false,
  })

  const handleChange = (event: any) => {
    const value = stripSpecialCharacters(event.target.value)
    if (props.maxLength && props.maxLength < value.length) {
      return false
    } else {
      setState((state) => ({
        ...state,
        value: value,
      }))
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      save()
    }

    if (event.key === 'Escape') {
      cancel()
    }
  }

  const save = async () => {
    _saved = true
    if (props.onSave != null && props.value !== state.value) {
      const value = state.value === null ? props.value : state.value

      props.onSave(value)
    }

    finishEdit()
  }

  const handleBlur = () => {
    setTimeout(() => {
      if (_canceled || _saved) {
        _canceled = false
        _saved = false

        return
      }

      if (props.saveOnBlur) {
        save()
      } else {
        finishEdit()
      }
    }, 200)
  }

  const cancel = async () => {
    _canceled = true
    finishEdit()
  }

  const edit = () => {
    _saved = false
    _canceled = false

    setState({
      editing: true,
      value: props.value,
    })
  }

  const finishEdit = () => {
    setTimeout(() => {
      if (state.value === null && state.editing === false) {
        return
      }

      setState({
        value: null,
        editing: false,
      })
    }, 10)
  }

  const getValue: () => string = () => {
    if (state.value != null) {
      return state.value
    }

    return props.value
  }

  return (
    <Box
      id="cp-headline-input"
      sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
      <Typography
        variant="subtitle1"
        align="left"
        sx={{
          fontWeight: 700,
          marginRight: '10px !important',
          fontSize: '12px',
        }}>
        {props.label}
      </Typography>
      {state.editing ? (
        <>
          <FormControl fullWidth={true} margin={'dense'}>
            {props.maxLength ? (
              <Box sx={{ position: 'relative' }}>
                <CounterBadge warning={getValue().length >= props.maxLength}>
                  {getValue().length}/{props.maxLength}
                </CounterBadge>
              </Box>
            ) : null}

            <MaterialTextField
              sx={{
                fontSize: '14px',
                maxWidth: '150px',
                marginLeft: 1,
                marginRight: 1,
              }}
              autoFocus={true}
              variant={'outlined'}
              value={getValue()}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              onBlur={handleBlur}
            />
          </FormControl>
          <HeaderIconEdit onClick={save}>check_circle</HeaderIconEdit>
          <HeaderIconEdit onClick={cancel}>cancel</HeaderIconEdit>
        </>
      ) : (
        <>
          <span id="cp-creative-name" style={{ wordBreak: 'break-all' }}>
            {props.value}
          </span>
          {props.onSave ? <EditButton onClick={edit} disabled={false} /> : null}
        </>
      )}
    </Box>
  )
}

export default Editable
